import React from 'react';
import type { NewCoreIconProps } from '@atlaskit/icon';
import ArchiveBoxIcon from '@atlaskit/icon/core/archive-box';
import ComponentIcon from '@atlaskit/icon/core/component';
import InboxIcon from '@atlaskit/icon/core/inbox';
import FormIcon from '@atlaskit/icon/core/form';
import GlobeIcon from '@atlaskit/icon/core/globe';
import GoalIcon from '@atlaskit/icon/core/goal';
import IncidentIcon from '@atlaskit/icon/core/incident';
import LinkIcon from '@atlaskit/icon/core/link';
import AngleBracketsIcon from '@atlaskit/icon/core/angle-brackets';
import AttachmentsIcon from '@atlaskit/icon/core/attachment';
import BacklogIcon from '@atlaskit/icon/core/backlog';
import BoardIcon from '@atlaskit/icon/core/board';
import CalendarIcon from '@atlaskit/icon/core/calendar';
import ChartTrendUpIcon from '@atlaskit/icon/core/chart-trend-up';
import ClockIcon from '@atlaskit/icon/core/clock';
import CloudArrowUpIcon from '@atlaskit/icon/core/cloud-arrow-up';
import DecisionIcon from '@atlaskit/icon/core/decision';
import IssuesIcon from '@atlaskit/icon/core/work-items';
import PageIcon from '@atlaskit/icon/core/page';
import ReleaseIcon from '@atlaskit/icon/core/release';
import RoadmapIcon from '@atlaskit/icon/core/roadmap';
import OnCallIcon from '@atlaskit/icon/core/on-call';
import ShieldIcon from '@atlaskit/icon/core/shield';
import TableIcon from '@atlaskit/icon/core/table';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { fg } from '@atlassian/jira-feature-gating';
import AppPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/app-preview/index.tsx';
import ApprovalsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/approvals-preview/index.tsx';
import AttachmentsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/attachments-preview/index.tsx';
import BacklogPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/backlog-preview/index.tsx';
import BoardPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/board-preview/index.tsx';
import CalendarPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/calendar-preview/index.tsx';
import CapacityPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/capacity-preview/index.tsx';
import CodePreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/code-preview/index.tsx';
import ComponentPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/component-preview/index.tsx';
import DeploymentsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/deployments-preview/index.tsx';
import DevelopmentPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/development-preview/index.tsx';
import FormsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/forms-preview/index.tsx';
import GoalsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/goals-preview/index.tsx';
import IncidentsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/incidents-preview/index.tsx';
import IssuesPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/issues-preview/index.tsx';
import ListPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/list-preview/index.tsx';
import OnCallPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/oncall-preview/index.tsx';
import PagesPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/pages-preview/index.tsx';
import ReleasesPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/releases-preview/index.tsx';
import ReportsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/reports-preview/index.tsx';
import SecurityPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/security-preview/index.tsx';
import ShortcutsPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/shortcuts-preview/index.tsx';
import SummaryPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/summary-preview/index.tsx';
import StaffingPreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/staffing-preview/index.tsx';
import TimelinePreview from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/view-types/components/timeline-preview/index.tsx';
import type { TabsDescriptor } from '@atlassian/jira-horizontal-nav-common/src/common/types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import messages from './messages.tsx';

export const JIRA_TABS: TabsDescriptor = {
	APP: {
		Icon: (props: NewCoreIconProps) => (
			<DecisionIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: AppPreview,
		description: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.appDescriptionGalaxia
				: messages.appDescription,
	},
	APPROVALS: {
		Icon: (props: NewCoreIconProps) => (
			<DecisionIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof props.size === 'string' ? props.size : undefined}
			/>
		),
		Illustration: ApprovalsPreview,
		description: () => messages.approvalsDescription,
	},
	ARCHIVED_ISSUES: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<ArchiveBoxIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: IssuesPreview,
		description: () => {
			if (fg('jira-issue-terminology-refresh-m3')) {
				if (expVal('project-terminology-refresh', 'isEnabled', false)) {
					return messages.archivedIssuesDescriptionIssueTermRefreshGalaxia;
				}
				return messages.archivedIssuesDescriptionIssueTermRefresh;
			}
			return messages.archivedIssuesDescription;
		},
	},
	ATTACHMENTS: {
		Icon: (props: NewCoreIconProps) => (
			<AttachmentsIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof props.size === 'string' ? props.size : undefined}
			/>
		),
		Illustration: AttachmentsPreview,
		description: () => {
			if (fg('jira-issue-terminology-refresh-m3')) {
				if (expVal('project-terminology-refresh', 'isEnabled', false)) {
					return messages.attachmentsDescriptionIssueTermRefreshGalaxia;
				}
				return messages.attachmentsDescriptionIssueTermRefresh;
			}
			return messages.attachmentsDescription;
		},
	},
	BACKLOG: {
		Icon: (props: NewCoreIconProps) => (
			<BacklogIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: BacklogPreview,
		description: () => messages.backlogDescription,
	},
	BOARD: {
		Icon: (props: NewCoreIconProps) => (
			<BoardIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: BoardPreview,
		description: () => messages.boardDescription,
	},
	CALENDAR: {
		Icon: (props: NewCoreIconProps) => (
			<CalendarIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: CalendarPreview,
		description: () => messages.calendarDescription,
	},
	CAPACITY: {
		Icon: (props: NewCoreIconProps) => (
			<ClockIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: CapacityPreview,
		description: () => messages.capacityDescription,
	},
	CODE: {
		Icon: (props: NewCoreIconProps) => (
			<AngleBracketsIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: CodePreview,
		description: () => messages.codeDescription,
	},
	COMPONENTS: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<ComponentIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: ComponentPreview,
		description: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.componentsDescriptionGalaxia
				: messages.componentsDescription,
	},
	DEPLOYMENTS: {
		Icon: (props: NewCoreIconProps) => (
			<CloudArrowUpIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: DeploymentsPreview,
		description: () => messages.deploymentsDescription,
	},
	DEVELOPMENT: {
		Icon: (props: NewCoreIconProps) => (
			<AngleBracketsIcon
				label=""
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: DevelopmentPreview,
		description: () => messages.developmentDescription,
	},
	FORMS: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<FormIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: FormsPreview,
		description: () => messages.formsDescription,
	},
	GOALS: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<GoalIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: GoalsPreview,
		description: () => {
			if (fg('jira-issue-terminology-refresh-m3')) {
				if (expVal('project-terminology-refresh', 'isEnabled', false)) {
					return messages.goalsDescriptionIssueTermRefreshGalaxia;
				}
				return messages.goalsDescriptionIssueTermRefresh;
			}
			return messages.goalsDescription;
		},
	},
	INCIDENTS: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<IncidentIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: IncidentsPreview,
		description: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.incidentsDescriptionGalaxia
				: messages.incidentsDescription,
	},
	ISSUES: {
		Icon: (props: NewCoreIconProps) => (
			<IssuesIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: IssuesPreview,
		description: () => {
			if (fg('jira-issue-terminology-refresh-m3')) {
				if (expVal('project-terminology-refresh', 'isEnabled', false)) {
					return messages.issuesDescriptionIssueTermRefreshGalaxia;
				}
				return messages.issuesDescriptionIssueTermRefresh;
			}
			return messages.issuesDescription;
		},
	},
	LIST: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<TableIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: ListPreview,
		description: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.listDescriptionGalaxia
				: messages.listDescription,
	},
	ON_CALL: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<OnCallIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: OnCallPreview,
		description: () => {
			if (fg('enable_on_call_page_deprecation_content')) {
				return messages.onCallDescriptionDeprecationNote;
			}
			if (expVal('project-terminology-refresh', 'isEnabled', false)) {
				return messages.onCallDescriptionGalaxia;
			}
			return messages.onCallDescription;
		},
	},
	PAGES: {
		Icon: (props: NewCoreIconProps) => (
			<PageIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: PagesPreview,
		description: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.pagesDescriptionGalaxia
				: messages.pagesDescription,
	},
	RELEASES: {
		Icon: (props: NewCoreIconProps) => (
			<ReleaseIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: ReleasesPreview,
		description: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.releasesDescriptionGalaxia
				: messages.releasesDescription,
	},
	REPORTS: {
		Icon: (props: NewCoreIconProps) => (
			<ChartTrendUpIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: ReportsPreview,
		description: () => {
			if (fg('jira-issue-terminology-refresh-m3')) {
				if (expVal('project-terminology-refresh', 'isEnabled', false)) {
					return messages.reportsDescriptionIssueTermRefreshGalaxia;
				}
				return messages.reportsDescriptionIssueTermRefresh;
			}
			return messages.reportsDescription;
		},
	},
	SECURITY: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<ShieldIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: SecurityPreview,
		description: () => messages.securityDescription,
	},
	SHORTCUTS: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<LinkIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: ShortcutsPreview,
		description: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.shortcutsDescriptionGalaxia
				: messages.shortcutsDescription,
	},
	SUMMARY: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<GlobeIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: SummaryPreview,
		description: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.summaryDescriptionNonFinalGalaxia
				: messages.summaryDescriptionNonFinal,
	},
	STAFFING: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<PeopleGroupIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: StaffingPreview,
		description: () => messages.staffingDescription,
	},
	TIMELINE: {
		Icon: (props: NewCoreIconProps) => (
			<RoadmapIcon
				{...props}
				color="currentColor"
				spacing={props.size === 'small' ? 'none' : 'spacious'}
			/>
		),
		Illustration: TimelinePreview,
		description: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.timelineDescriptionGalaxia
				: messages.timelineDescription,
	},
	INBOX: {
		Icon: ({ size, ...props }: NewCoreIconProps) => (
			<InboxIcon
				{...props}
				color="currentColor"
				spacing={size === 'small' ? 'none' : 'spacious'}
				LEGACY_size={typeof size === 'string' ? size : undefined}
			/>
		),
		Illustration: IssuesPreview,
		description: () => {
			return expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.inboxDescriptionGalaxia
				: messages.inboxDescription;
		},
	},
};
